import cls from './banner.module.scss';
import { useCachedSelectedProject, useTranslation } from 'hooks';
import Button from 'shared/ui/button/button';
import { formatDate } from 'shared/helpers/format-date';
import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { setActiveReport } from 'store/slices/historySlice';
import { useHistoryModeToggle } from 'shared/hooks/useHistoryModeToggle';
import { Modal } from 'shared/ui/modal/modal';
import { Icon } from 'shared/ui/icon/icon';
import { ICONS } from 'shared/constants/images';
import { RIVA_VERSION } from 'shared/constants/app';
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from '@headlessui/react';
import { Link } from 'react-router-dom';
import { PaidStatus, ReportInfo } from 'store/api/apiTypes';
import { appRoute } from 'shared/constants/routes';
import { ExportModal } from 'widgets/export-modal/export-modal';
import { Filter } from 'shared/constants/color-filters';
import { IconTooltip } from 'shared/ui/icon-tooltip/icon-tooltip';

const isStandalone = process.env.REACT_APP_TYPE === 'standalone';

export const Banner = () => {
  const { t } = useTranslation();
  const [expiredModal, setExpiredModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState<ReportInfo | null>(null);
  const dispatch = useAppDispatch();
  const activeReport = useAppSelector(state => state.history.activeReport);
  const { historyMode, previousReport, handleToggleHistoryMode } =
    useHistoryModeToggle();
  const {
    lastReport,
    selectedProject,
    isTrial,
    isTrialExpired,
    generationAvailableDate,
    showHistory,
    allowGeneration,
  } = useCachedSelectedProject();
  const [isTrialCTAOpen, setIsTrialCTAOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const dateCreatedStr = formatDate(new Date(lastReport?.date || ''), 'long');
  const isDashboard = window.location.href.includes('dashboard');

  const handleReportChange = (report: ReportInfo | null) => {
    if (!selectedProject?.reports) {
      return;
    }
    setSelectedReport(report);
    dispatch(setActiveReport(report ?? undefined));
  };

  useEffect(() => {
    if (activeReport) {
      setSelectedReport(activeReport);
    } else if (previousReport) {
      setSelectedReport(previousReport);
    }
  }, [activeReport, previousReport]);

  return (
    <div className={cls.banner}>
      <div
        className={`${cls.reportCreated} ${historyMode ? cls.historyMode : ''}`}
      >
        {historyMode ? (
          <>
            <div className={cls.text}>
              <Icon pathToIcon={ICONS.timer} />
              <p>{t('viewingPastReport')} </p>
              {selectedProject?.reports &&
                selectedProject.reports.length > 0 && (
                  <Listbox
                    as='div'
                    value={selectedReport}
                    onChange={handleReportChange}
                    className={cls.listBoxMain}
                  >
                    {({ open }) => (
                      <>
                        <ListboxButton
                          className={cls.listboxBtn}
                          data-testid='banner-listbox-trigger'
                        >
                          <p>
                            {formatDate(
                              new Date(selectedReport?.date ?? ''),
                              'dateTime'
                            )}
                          </p>
                          <Icon
                            className={open ? cls.open : ''}
                            pathToIcon={ICONS.arrowDown}
                            size={18}
                          />
                        </ListboxButton>
                        <ListboxOptions
                          anchor={{ to: 'bottom start', gap: 8 }}
                          className={cls.listboxOptions}
                        >
                          {selectedProject.reports &&
                            selectedProject.reports
                              .filter(
                                r => r.status !== 'ERROR' && r.status !== 'BUSY'
                              )
                              .map(r => {
                                return (
                                  <ListboxOption key={r.id} value={r}>
                                    {({ selected }) => (
                                      <div
                                        className={`${cls.listboxOption} ${selected ? cls.selected : ''}`}
                                      >
                                        <p>
                                          {formatDate(
                                            new Date(r.date),
                                            'dateTime'
                                          )}
                                        </p>
                                      </div>
                                    )}
                                  </ListboxOption>
                                );
                              })}
                        </ListboxOptions>
                      </>
                    )}
                  </Listbox>
                )}

              <Button
                theme='tonal'
                tone='default'
                className={cls.exportBtn}
                onClick={
                  isTrial || isTrialExpired
                    ? () => setIsTrialCTAOpen(true)
                    : () => setIsOpen(true)
                }
              >
                <Icon
                  pathToIcon={ICONS.download}
                  size={18}
                  filter={Filter.onSurfaceVariant}
                />
                {t('exportPDF')}
              </Button>
            </div>

            <Button
              theme='text'
              tone='default'
              onClick={handleToggleHistoryMode}
            >
              {t('exitReportHistory')}
            </Button>
          </>
        ) : (
          <>
            <div className={cls.rightSection}>
              <p>{t('report_created')}</p>
              <span>{dateCreatedStr}</span>
              <IconTooltip
                iconPath={ICONS.infoOutline}
                id='report-version-tooltip'
                iconSize={16}
                wrapperClassName={cls.tooltip}
                variant='light'
                tooltipClassName={cls.whiteTooltip}
                noArrow
                opacity={1}
                maxWidth={'250px'}
              >
                <span>
                  {t('analysedByRivaTooltip', undefined, undefined, {
                    version: RIVA_VERSION,
                  })}
                </span>
              </IconTooltip>
              <Button
                theme='tonal'
                tone='default'
                className={cls.exportBtn}
                onClick={
                  isTrial || isTrialExpired
                    ? () => setIsTrialCTAOpen(true)
                    : () => setIsOpen(true)
                }
              >
                <Icon
                  pathToIcon={ICONS.download}
                  size={18}
                  filter={Filter.onSurfaceVariant}
                />
                {t('exportPDF')}
              </Button>

              <span className={cls.available}> </span>
              <p>{t('report_available_from')}</p>
              {isTrial ? (
                t('afterUpgrade')
              ) : (
                <span>
                  {allowGeneration
                    ? t('available')
                    : formatDate(generationAvailableDate, 'long')}
                </span>
              )}
            </div>

            <div className={cls.buttons}>
              {selectedProject?.paid === PaidStatus.PAID && showHistory && (
                <Button
                  theme={'text'}
                  tone={'default'}
                  onClick={handleToggleHistoryMode}
                >
                  {t('reportHistory')}
                </Button>
              )}
              <Link to={appRoute('/offerings')} className={cls.link}>
                {isDashboard ? t('setupNextReport') : t('manageOfferings')}
              </Link>
            </div>
            <Modal
              width={498}
              isOpen={expiredModal}
              onClose={() => setExpiredModal(false)}
            >
              <div className={cls.expiredModal}>
                <h2>
                  {t(
                    isTrialExpired
                      ? 'scoreTrialExpiredBanner'
                      : 'trialUpgradeBanner'
                  )}
                </h2>
                <p>{t('trialExpiredBannerDescription')}</p>
                <div className={cls.buttonGroup}>
                  <Button onClick={() => setExpiredModal(false)}>
                    {t('close')}
                  </Button>
                  <Link to={appRoute('/payment')}>{t('upgrade')}</Link>
                </div>
              </div>
            </Modal>
          </>
        )}
      </div>
      {isTrialCTAOpen && (
        <Modal
          isOpen={isTrialCTAOpen}
          onClose={() => setIsTrialCTAOpen(false)}
          className={cls.trialCTAModal}
        >
          <h2>{t('exportPdfTrialCTATitle')}</h2>
          <p>{t('exportPdfTrialCTADescription')}</p>
          <div className={cls.trialCTAButtons}>
            <Button
              theme='text'
              tone='default'
              size='m'
              onClick={() => setIsTrialCTAOpen(false)}
            >
              {t('cancel')}
            </Button>
            <Link
              to={
                isStandalone ? appRoute('/subscriptions') : appRoute('/payment')
              }
              className={cls.trialCTALink}
            >
              {t('manageSubscriptions')}
            </Link>
          </div>
        </Modal>
      )}
      {isOpen && (
        <ExportModal isOpen={isOpen} onClose={() => setIsOpen(false)} />
      )}
    </div>
  );
};
